import { useQuery, useReactiveVar } from "@apollo/client";
import { currentUserVar } from "apollo/cache/auth";
import { LocationGenerics } from "router/location";
import config from "config";
import {
  dispatchModal,
  useTableData,
  useUrlState,
  wrapClick,
} from "utils";
import { Avatar, SearchSelectInput, StatusBadge } from "components/core";
import { ActionButton } from "components/buttons";
import { OfficeHeader, SummaryTabs } from "components/layouts";
import { TableComponent } from "components/tables";
import { Shimmers, Button } from "components";
import lodash from "lodash";
import moment from "moment";
import { FC, useEffect, useMemo } from "react";
import { useNavigate, useSearch } from "react-location";
import { GET_ISSUES, GET_ISSUES_SUMMARY } from "./apollo";
import _ from "lodash";
import ViewTicketContainer from "./view";
import {PlusIcon, MegaphoneIcon} from "@heroicons/react/24/outline";
import CreateIssueContainer from "./create";
import ApproveIssueContainer from "./approve";
import UpdateIssueContainer from "./update";

const ticketStages = [
  { name: "Reported To Focal", href: "ReportedToFocalPerson" },
  { name: "Referred To Power", href: "ReferredToPowerUser" },
  { name: "Escalated To Polymorph", href: "EscalatedToPolymorphLabs" },
  { name: "Resolved", href: "Resolved" },
  { name: "Completed", href: "Completed" },
];

const altTicketStages = [{ name: "Reassigned", href: "ReassignedToPowerUser" }];

const searchOptions = [
  {
    label: "Code",
    value: "code",
    min: 4,
  },
  {
    label: "Ticket ID",
    value: "clickupTicketId",
    min: 5,
  },
];

export const ZeusOperationalIssuesPage: FC = () => {

  const searchParams = useSearch<LocationGenerics>();
  const navigate = useNavigate<LocationGenerics>();
  const [modal, setModal] = useUrlState<string | undefined>("modal");
  const [ticketStage] = useUrlState("ticketStage");
  const [priority, setPriority] = useUrlState("priority");
  const [fetchBy, setFetchBy] = useUrlState("fetchBy");
  const currentUser = useReactiveVar(currentUserVar);

  const filter = useMemo(
    () => ({
      page: Number(searchParams.page || config.constants.page),
      pageSize: Number(searchParams.pageSize || config.constants.pageSize),
      search: searchParams.search || undefined,
      searchFields: searchParams.searchField
        ? [searchParams.searchField]
        : lodash.map(searchOptions, "value"),
      sort: searchParams.sort || "",
      currentStage: searchParams.ticketStage || undefined,
      classification: searchParams.classification || undefined,
      fetchBy: searchParams.fetchBy || "All",
    }),
    [searchParams]
  );

  const { data, loading, networkStatus, refetch } = useQuery(GET_ISSUES, {
    variables: filter,
    notifyOnNetworkStatusChange: true,
    onCompleted: () => {
      refetchSummary();
    },
  });

  const { data: dataSummary, refetch: refetchSummary } = useQuery(
    GET_ISSUES_SUMMARY,
    {
      variables: filter,
      notifyOnNetworkStatusChange: true,
    }
  );

  const records = useTableData(data);

  const onSearch = (searchValue: string) => {
    navigate({
      search: (old) => ({
        ...old,
        page: Number(config.constants.page || 1),
        pageSize: Number(searchParams.pageSize || config.constants.page),
        search: searchValue?.toString(),
      }),
    });
  };

  useEffect(() => {
    navigate({
      search: (old) => ({
        ...old,
        ...(!old?.page ? { page: config.constants.page } : {}),
        ...(!old?.pageSize ? { pageSize: config.constants.pageSize } : {}),
        ...(!old?.search ? {} : { search: "" }),
        ...(!old?.sort ? { sort: "-createdAt" } : {}),
        ...(!old?.fetchBy ? { fetchBy: "All" } : {}),
      }),
    });
  }, [navigate]);

  return (
    <main className="flex-1 flex flex-col overflow-hidden bg-gray-50">
      <OfficeHeader
        renderActions={() => (
          <>
            <button
              type="button"
              onClick={wrapClick(() => setModal("create"))}
              className="ml-3 inline-flex items-center rounded-md border border-transparent bg-primary-600 px-3 py-2 text-sm leading-4 text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" aria-hidden="true" />
              Create Issue
            </button>
            {
              currentUser?.isPowerUser && (
                <Button
                  theme={'neutral'}
                  onClick={wrapClick(() => setModal("report-directly"))}
                >
                  <span className={'inline-flex items-center gap-2'}>
                    <MegaphoneIcon className="-ml-0.5 mr-2 h-4 w-4" aria-hidden="true"/>
                    Report Issue Directly
                  </span>
                </Button>
              )
            }
          </>
        )}
      />

      <SummaryTabs
        tabs={ticketStages}
        altTabs={altTicketStages}
        summary={dataSummary?.summary}
        param={"ticketStage"}
        label="Issues"
      />
      <div className="flex flex-1 overflow-y-auto">
        <div className="flex-1 min-h-full mx-auto min-w-0  py-4 sm:py-6 overflow-hidden overflow-y-auto light flex">
          <TableComponent
            title={"tickets"}
            refetch={refetch}
            isRefetching={loading && networkStatus === 4}
            loading={loading && ![4, 6].includes(networkStatus)}
            data={records}
            hasSearch={true}
            onSearchClicked={onSearch}
            renderHeaderItems={() => (
              <>
                <div className="mt-4 sm:mt-0.5 sm:ml-4 sm:flex space-x-2">
                  <div>
                    <SearchSelectInput
                      id="priority"
                      options={[
                        { label: { title: "Low" }, value: "Low" },
                        { label: { title: "Normal" }, value: "Normal" },
                        { label: { title: "High" }, value: "High" },
                        { label: { title: "Urgent" }, value: "Urgent" },
                      ]}
                      placeholder="Select Priority"
                      setFieldValue={(_: any, value: string) =>
                        setPriority(value)
                      }
                      values={{ priority }}
                      labelHidden
                      label={""}
                    />
                  </div>
                </div>

                <div className="mt-4 sm:mt-0.5 sm:ml-4 sm:flex space-x-2">
                  <div>
                    <SearchSelectInput
                      id="fetchBy"
                      options={[
                        { label: { title: "All" }, value: "All"},
                        {
                          label: { title: "Assigned to Me" },
                          value: "AssignedToMe"
                        },
                        { label: { title: "Created By Me" }, value: "CreatedByMe"},
                      ]}
                      placeholder="Select Fetch By"
                      setFieldValue={(_: any, value: string) =>
                        setFetchBy(value)
                      }
                      values={{ fetchBy }}
                      labelHidden
                      label={""}
                    />
                  </div>
                </div>
              </>
            )}
            renderColumns={() => (
              <tr>
                <th
                  scope="col"
                  className="sticky top-0 border-y border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                >
                  Code | Date
                </th>
                <th
                  scope="col"
                  className="sticky top-0 border-y border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                >
                  Classification
                </th>
                <th
                  scope="col"
                  className="sticky top-0 border-y border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                >
                  Application | Ticket ID
                </th>
                <th
                  scope="col"
                  className="sticky top-0 border-y border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                >
                  Status
                </th>
                <th
                  scope="col"
                  className="sticky top-0 border-y border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                >
                  Priority
                </th>

                {["Rejected"].includes(ticketStage) && (
                  <th
                    scope="col"
                    className="sticky top-0 border-y border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-right text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                  >
                    Rejected By
                  </th>
                )}

                {["Completed", "Rejected"].includes(ticketStage) && (
                  <th
                    scope="col"
                    className="sticky top-0 border-y border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                  >
                    {ticketStage === "Rejected" ? "Rejected By" : "Approved By"}{" "}
                  </th>
                )}

                {!ticketStage && (
                  <th
                    scope="col"
                    className="sticky top-0 border-y border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                  >
                    Stage
                  </th>
                )}
                <th
                  scope="col"
                  className="sticky top-0 border-y border-gray-200 bg-gray-50 dark:bg-gray-800 px-6 py-3 text-left text-xs font-light text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                ></th>
              </tr>
            )}
            renderLoader={() => (
              <tr>
                <td className="px-6 py-4 border-b border-gray-200">
                  <Shimmers.DoubleShimmer />
                </td>
                <td className="px-6 py-4 border-b border-gray-200">
                  <Shimmers.DoubleShimmer />
                </td>
                <td className="px-6 py-4 border-b border-gray-200">
                  <Shimmers.DoubleShimmer />
                </td>
                <td className="px-6 py-4 border-b border-gray-200">
                  <Shimmers.DoubleShimmer />
                </td>
                <td className="px-6 py-4 border-b border-gray-200">
                  <Shimmers.SingleShimmer />
                </td>
                {["Assigned", "InProgress", "Resolved"].includes(
                  ticketStage
                ) && (
                  <td className="px-6 py-4 border-b border-gray-200">
                    <Shimmers.DoubleShimmer />
                  </td>
                )}

                {["Rejected"].includes(ticketStage) && (
                  <td className="px-6 py-4 border-b border-gray-200">
                    <Shimmers.DoubleShimmer />
                  </td>
                )}
                {["Completed"].includes(ticketStage) && (
                  <td className="px-6 py-4 border-b border-gray-200">
                    <Shimmers.DoubleShimmer />
                  </td>
                )}
                {["Completed", "Rejected"].includes(ticketStage) && (
                  <td className="px-6 py-4 border-b border-gray-200">
                    <Shimmers.AvatarShimmer />
                  </td>
                )}

                {!ticketStage && (
                  <td className="px-6 py-4 border-b border-gray-200">
                    <Shimmers.SingleShimmer />
                  </td>
                )}
                <td className="px-6 py-4 border-b border-gray-200">
                  <Shimmers.ActionsShimmer actionsCount={4} />
                </td>
              </tr>
            )}
            renderItem={(item) => (
              <tr
                key={item._id}
                className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                onClick={wrapClick(dispatchModal(item._id, "view"))}
              >
                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200 ">
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {item?.code || "N/A"}
                  </div>
                  <div className="font-medium text-gray-500 dark:text-gray-400">
                    {moment(item?.createdAt).format("DD/MM/YYYY")}
                  </div>
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200">
                  <div className="text-gray-900 dark:text-gray-100">
                    {lodash.startCase(item?.classification) || "N/A"}
                  </div>
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200">
                  <div className="text-gray-900 dark:text-gray-100">
                    {_.startCase(item?.issueApplication) || "N/A"}
                  </div>
                  {item?.clickupTicketId && (
                    <div className="font-medium text-gray-500 dark:text-gray-400">
                      {item?.clickupTicketId || "N/A"}
                    </div>
                  )}
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200">
                  <div className="text-gray-900 dark:text-gray-100">
                    {lodash.startCase(item?.status) || "N/A"}
                  </div>
                  <div className="text-gray-500 dark:text-gray-500">
                    {moment(item?.currentStage?.timestamp).format("DD/MM/YYYY")}
                  </div>
                </td>
                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200">
                  <StatusBadge
                    status={item?.priority}
                    options={{
                      High: "bg-orange-100 text-orange-800",
                      Low: "bg-gray-100 text-gray-800",
                      Urgent: "bg-red-100 text-red-800",
                      Normal: "bg-yellow-100 text-yellow-800",
                    }}
                  />
                </td>
                {["Open", "InProgress", "Closed"].includes(ticketStage) && (
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200 text-right">
                    <div className="text-gray-900 dark:text-gray-100">
                      {item?.assignedAt
                        ? moment(item?.assignedAt).format("DD/MM/YYYY")
                        : "N/A"}
                    </div>
                    <div className="text-gray-500 dark:text-gray-400">
                      {item?.estimatedResolutionDate
                        ? moment(item?.estimatedResolutionDate).format(
                            "DD/MM/YYYY"
                          )
                        : "N/A"}
                    </div>
                  </td>
                )}

                {["Completed", "Rejected"].includes(ticketStage) && (
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200 ">
                    <div className="flex items-center">
                      <div className="h-10 w-10 flex-shrink-0">
                        <Avatar
                          disabled={true}
                          alt={
                            [
                              item?.currentStage?.user?.lastName,
                              item?.currentStage?.user?.firstName,
                            ]
                              .join(" ")
                              .trim() || "N A"
                          }
                          src={item?.currentStage?.user?.profileImageUrl}
                        />
                      </div>
                      <div className="ml-4">
                        <div className="text-gray-900 dark:text-gray-100">
                          {[
                            item?.currentStage?.user?.lastName,
                            item?.currentStage?.user?.firstName,
                          ]
                            .join(" ")
                            .trim() || "N A"}
                        </div>
                        <div className="text-gray-500 dark:text-gray-400">
                          {item?.currentStage?.user?.phoneNumber || "N/A"}
                        </div>
                      </div>
                    </div>
                  </td>
                )}

                {!ticketStage && (
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200 ">
                    <StatusBadge
                      status={
                        lodash.startCase(item?.currentStage.stage) || "N/A"
                      }
                      options={{
                        Failed: "bg-orange-100 text-orange-800",
                        Passed: "bg-green-100 text-green-800",
                      }}
                    />
                  </td>
                )}
                <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 border-b border-gray-200 text-right">
                  <div className="space-x-1">
                    <ActionButton
                      action="view"
                      onClick={dispatchModal(item?._id, "view")}
                    />

                    {item?.createdBy === currentUser?._id &&
                      ["Resolved"].includes(ticketStage) && (
                        <>
                          <ActionButton
                            action="approve"
                            onClick={dispatchModal(item?._id, "approve")}
                          />
                          <ActionButton
                            action="disapprove"
                            onClick={dispatchModal(item?._id, "disapprove")}
                          />
                        </>
                      )}
                  </div>
                </td>
              </tr>
            )}
          />
        </div>
      </div>

      <CreateIssueContainer
        open={modal === "create"}
        setOpen={(val: boolean) => setModal(val ? "create" : undefined)}
        refetch={refetch}
      />

      {Boolean(searchParams.id?.length) && (
        <>
          <ViewTicketContainer
            open={modal === "view"}
            setOpen={(val: boolean) => setModal(val ? "view" : undefined)}
          />
          <ApproveIssueContainer
            open={modal === "approve"}
            setOpen={(val: boolean) => setModal(val ? "approve" : undefined)}
            refetch={refetch}
          />
          <UpdateIssueContainer
            open={modal === "update"}
            setOpen={(val: boolean) => setModal(val ? "update" : undefined)}
            refetch={refetch}
          />
        </>
      )}
    </main>
  );
};

export default ZeusOperationalIssuesPage;
